import React from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { useModal } from '../context/ModalContext';
import { solutionPages, solutionPagesBySlug } from '../data/solutions';
const SolutionDetail = () => {
  const { solutionSlug } = useParams();
  const solution = solutionPagesBySlug[solutionSlug];
  const { openDemoModal } = useModal();
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.08 });

  if (!solution) {
    return (
      <section className="relative py-24 md:py-28 bg-[--bg-primary] min-h-[65vh]">
        <div className="absolute top-0 left-0 right-0 section-divider" />
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6">
          <div className="soft-card rounded-2xl p-10 max-w-3xl">
            <p className="section-eyebrow mb-4">Solutions</p>
            <h1 className="text-3xl font-semibold text-[--text-primary] leading-tight mb-4">
              Solution not found
            </h1>
            <p className="text-[--text-secondary] mb-8">
              This solution page does not exist. Browse all available solution categories.
            </p>
            <Link
              to="/solutions"
              className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] font-mono text-[--accent] hover:gap-3 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Solutions
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const relatedSolutions = solutionPages.filter((item) => item.slug !== solution.slug).slice(0, 4);

  return (
    <section className="relative py-24 md:py-28 bg-[--bg-primary]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <Link
          to="/solutions"
          className={`inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] font-mono text-[--text-muted] hover:text-[--text-primary] mb-8 transition-all duration-700 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          All Solutions
        </Link>

        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-6 lg:gap-8">
          <div
            className={`soft-card rounded-2xl p-8 lg:p-10 transition-all duration-700 ease-out ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <p className="section-eyebrow mb-4">Solution</p>
            <h1 className="text-[clamp(2rem,4.1vw,3.5rem)] font-semibold text-[--text-primary] leading-[1.02] mb-5">
              {solution.name}
            </h1>
            <p className="text-[--text-secondary] text-lg leading-relaxed mb-10">
              {solution.overview}
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-colors flex items-center gap-2 btn-pattern"
              >
                Book a Free Audit
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="/#case-studies"
                className="px-7 py-3.5 text-[11px] font-medium text-[--text-primary] uppercase tracking-[0.18em] font-mono border border-[--border] rounded-full hover:border-[--border-hover] hover:bg-[--bg-secondary] transition-colors"
              >
                View Case Studies
              </a>
            </div>

            {solution.proofPoints?.length > 0 && (
              <div className="mt-10 grid sm:grid-cols-3 gap-3">
                {solution.proofPoints.map((point) => (
                  <div
                    key={point.value + point.label}
                    className="rounded-xl border border-[--border] bg-[--bg-secondary] px-4 py-4"
                  >
                    <p className="text-xl font-semibold text-[--accent] leading-none mb-2">
                      {point.value}
                    </p>
                    <p className="text-xs text-[--text-secondary] leading-relaxed">
                      {point.label}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={`soft-card rounded-2xl p-8 lg:p-10 transition-all duration-700 ease-out delay-100 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <p className="section-eyebrow mb-5">Core Workflows</p>
            <ul className="space-y-4 mb-10">
              {solution.workflows.map((workflow) => (
                <li key={workflow} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 text-[--accent] shrink-0" />
                  <span className="text-[--text-secondary] leading-relaxed">{workflow}</span>
                </li>
              ))}
            </ul>

            <div className="h-px bg-[--border] mb-8" />

            <p className="section-eyebrow mb-5">Expected Outcomes</p>
            <ul className="space-y-3">
              {solution.outcomes.map((outcome) => (
                <li key={outcome} className="text-[--text-secondary] text-sm leading-relaxed">
                  • {outcome}
                </li>
              ))}
            </ul>

            {solution.useCases?.length > 0 && (
              <>
                <div className="h-px bg-[--border] my-8" />
                <p className="section-eyebrow mb-5">High-Impact Use Cases</p>
                <ul className="space-y-3">
                  {solution.useCases.map((useCase) => (
                    <li key={useCase} className="text-[--text-secondary] text-sm leading-relaxed">
                      • {useCase}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div
          className={`mt-8 lg:mt-10 transition-all duration-700 ease-out delay-200 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <div className="soft-card rounded-2xl p-6 lg:p-7">
            <p className="section-eyebrow mb-5">Explore Related Solutions</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {relatedSolutions.map((item) => (
                <Link
                  key={item.slug}
                  to={`/solutions/${item.slug}`}
                  className="group rounded-xl border border-[--border] bg-transparent hover:bg-[--bg-secondary] hover:border-[--border-hover] px-4 py-4 transition-colors"
                >
                  <p className="text-sm font-medium text-[--text-primary] group-hover:text-[--accent] transition-colors">
                    {item.name}
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-mono text-[--text-muted] group-hover:text-[--text-secondary] transition-colors">
                    Open
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionDetail;
